import { isPlatformBrowser } from '@angular/common';
import {
  Directive,
  TemplateRef,
  ViewContainerRef,
  Input,
  OnInit,
  Inject,
  PLATFORM_ID,
  EmbeddedViewRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';

import { LazyLoadObserverService } from './lazy-load-observer.service';

/**
 * A structural directive that lazy loads its content. Meanwhile, a template reference is used as skeleton
 * that can be used to avoid content reflow.
 *
 * A [shorthand form](https://angular.io/guide/structural-directives#the-asterisk--prefix) of the directive,
 * `*idtLazyTemplate="templateRef"`, is generally used. Angular expands this into a more explicit version,
 * in which the anchor element * is contained in an `<ng-template>` element.
 *
 * Simple form with shorthand syntax:
 *
 * ```
 * <div *idtLazyTemplate="defaultTemplate">Content to lazy load.</div>
 * <ng-template #defaultTemplate></ng-template>
 * ```
 *
 * Simple form with expanded syntax:
 *
 * ```
 * <ng-template [idtLazyTemplate]="defaultTemplate">
 *  <div>
 *    Content to render when condition is * true.
 *  </div>
 * </ng-template>
 * <ng-template #defaultTemplate></ng-template>
 * ```
 *
 * The presence of the implicit template object has implications for the nesting of
 * structural directives. For more on this subject, see
 * [Structural Directives](https://angular.io/guide/structural-directives#one-per-element).
 *
 */
@Directive({ selector: '[idtLazyTemplate]' })
export class LazyLoadTemplateDirective implements OnInit, AfterViewInit, OnDestroy {

  @Input() public idtLazyTemplate!: TemplateRef<unknown>;

  private readonly loadedTemplateRef!: TemplateRef<unknown>;
  private notLoadedViewRef!: EmbeddedViewRef<unknown>;
  private loadedViewRef!: EmbeddedViewRef<unknown>;

  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private readonly template: TemplateRef<unknown>,
    private readonly viewContainer: ViewContainerRef,
    private readonly lazyLoadObserverService: LazyLoadObserverService,
    @Inject(PLATFORM_ID) private readonly platformId: Record<string, unknown>
  ) {
    this.loadedTemplateRef = template;
  }

  public ngOnInit() {
    this.viewContainer.clear();
    this.notLoadedViewRef = this.viewContainer.createEmbeddedView(this.idtLazyTemplate);
  }

  public ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) {
      this.renderElement();
    }

    if (this.notLoadedViewRef.rootNodes.length) {
      this.lazyLoadObserverService.observeElement(
        this.notLoadedViewRef.rootNodes[this.notLoadedViewRef.rootNodes.length - 1]
      ).pipe(
        takeUntil(this.ngUnsubscribe),
        take(1)
      ).subscribe(() => this.renderElement());
    }
  }

  public ngOnDestroy() {
    this.lazyLoadObserverService.unobserveElement(this.notLoadedViewRef.rootNodes[this.notLoadedViewRef.rootNodes.length - 1]);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private renderElement() {
    this.notLoadedViewRef?.destroy();
    this.loadedViewRef = this.viewContainer.createEmbeddedView(this.loadedTemplateRef);
    this.loadedViewRef.detectChanges();
    this.loadedViewRef.markForCheck();
  }

}
